import "../../../components/common/Tables/DataTable.css"
import React, {useState} from "react";
import { useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { Tooltip } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CategoryLabels } from "../categoryLabels.enum";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import useRoleCheck from "../../../hooks/useRoleCheck";
import { localizePal } from "../../../utils/brand";
import { DataTableFoot, SortButton } from "../../../components/common/Tables/DataTableParts";
import { BugIcon, TrashIcon } from "../../../components/common/Icons/Icons";

type Order = 'asc' | 'desc';
const baseHeaders = [
  {id:'is_unread', label:'', align:"center"},
  {id:'title', label:'Title',align:null},
  {id:'body', label:'Body',align:null},
  {id:'user', label:'Author',align:null},
  {id:'category', label:'Category',align:null},
  {id:'spa_id', label:'Spa', align:null},
  {id:'updated_at', label:'Last Updated',align:null},
  {id:'created_at', label:'Created',align:null},
  {id:'delete', label:'Delete', align:"center"},
];

// Delete is pinned to the right edge (px, fits the heading)
const actionWidths: Record<string, number> = {
  delete: 68,
};

interface TableProps {
  setViewOpen: any;
  setDeleteOpen: any;
  setComment: any;
  // Page controls (filters toggle, Add Comment) shown in the table's toolbar
  toolbar?: React.ReactNode;
}

const CommentsTable: React.FC<TableProps> = (
  {
    setViewOpen,
    setDeleteOpen,
    setComment,
    toolbar,
  }
) => {

  const {data:comments, loading, meta} = useSelector((state: RootState) => state.comments);
  const {data:user} = useSelector((state: RootState) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  // Table Sort (read from the URL so the arrow survives a reload)
  const [order, setOrder] = useState<Order>(searchParams.get('sort_direction') === 'desc' ? 'desc' : 'asc');
  const [orderBy, setOrderBy] = useState(searchParams.get('sort_by') ?? '');
  const {canAccess:canDeleteComment} = usePermissionCheck(['delete_any_comment']);
  const {canAccess: isDeveloper} = useRoleCheck(['developer']);

  const {id} = useParams();
  const navigate = useNavigate();

  // A single spa's comments don't need the Spa column
  const headers = baseHeaders.filter((header)=>!(id && header.id === 'spa_id'));

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const totalPages = meta?.total_pages ?? 1;
  const total = meta?.total ?? 0;

  const handleRequestSort = (header_id: string) => {
    const isAsc = orderBy === header_id && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(header_id);
    searchParams.set("sort_direction", newOrder);
    searchParams.set("sort_by", header_id);
    setSearchParams(searchParams);
  };

  // Keeps the filters and sort (the old pager replaced the whole query with ?page=)
  const goToPage = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
  }

  const openComment = (comment: any) => {
    setComment(comment);
    setViewOpen(true);
  }

  const pinned = headers.map((header)=>header.id).filter((id)=>id in actionWidths);

  const pinStyle = (id: string) => {
    const index = pinned.indexOf(id);
    if (index < 0) return undefined;
    const right = pinned.slice(index + 1).reduce((sum, next) => sum + actionWidths[next], 0);
    return {'--dt-right': `${right}px`, '--dt-w': `${actionWidths[id]}px`} as React.CSSProperties;
  };

  const cellClass = (header: typeof headers[number]) => [
    header.align ? 'dt-center' : '',
    pinned.includes(header.id) ? 'dt-pin' : '',
    pinned[0] === header.id ? 'dt-pin-start' : '',
  ].filter(Boolean).join(' ') || undefined;

  // Links inside a clickable row: follow the link, don't also open the comment
  const appLink = (path: string, className: string, children: React.ReactNode) => (
    <a
      href={path}
      className={className}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        e.stopPropagation();
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        navigate(path);
      }}
    >
      {children}
    </a>
  );

  const authorName = (comment: any) => comment?.user?.name ?? comment?.user?.username ?? comment?.user?.email ?? 'N/A';

  const renderCell = (id: string, comment: any) => {
    switch (id) {
      case 'is_unread':
        return (
          <span className="dt-flags">
            <span className={`dt-unread-dot ${comment?.is_unread ? 'on' : ''}`} title={comment?.is_unread ? 'Unread' : 'Read'}/>
            <span className="visually-hidden">{comment?.is_unread ? 'Unread' : 'Read'}</span>
            {/* The API sends these flags as 0/1, so coerce them or React prints the 0 */}
            {isDeveloper && !!comment?.dev_only &&
              <Tooltip title="Dev only" arrow><span className="dt-dev"><BugIcon/><span className="visually-hidden">Dev only</span></span></Tooltip>}
            {!!comment?.is_resolved && <span className="dt-pill good">Resolved</span>}
          </span>
        );

      case 'title':
        // A real button so the comment opens from the keyboard too
        return (
          <button type="button" className="dt-title-btn" onClick={(e)=>{e.stopPropagation(); openComment(comment);}}>
            {comment?.title}
          </button>
        );

      case 'body':
        return <span className="dt-body" title={comment?.body}>{comment?.body}</span>;

      case 'user':
        return (
          <Tooltip
            arrow
            title={
              <div>
                <strong>{authorName(comment)}</strong>
                {comment?.user?.email && <div>{comment.user.email}</div>}
                {comment?.user?.username && <div>{comment.user.username}</div>}
              </div>
            }
          >
            {appLink(`/user/${comment?.user?.id}`, 'dt-link', authorName(comment))}
          </Tooltip>
        );

      case 'category': {
        const label = CategoryLabels[comment?.category as keyof typeof CategoryLabels];
        return label ? <span className="dt-tag">{localizePal(label)}</span> : null;
      }

      case 'spa_id':
        return appLink(
          `/spa/${comment?.spa_id}`,
          'dt-link',
          comment?.spa?.nick_name ? `${comment?.spa?.nick_name} (${comment?.spa?.arctic_serial_number})` : `${comment?.spa?.arctic_serial_number}`,
        );

      case 'updated_at':
        return <span className="dt-date">{new Date(comment?.updated_at).toLocaleString()}</span>;

      case 'created_at':
        return <span className="dt-date">{new Date(comment?.created_at).toLocaleString()}</span>;

      case 'delete':
        return (canDeleteComment || user?.id===comment?.user?.id) ? (
          <Tooltip title="Delete comment" arrow>
            <button
              type="button"
              className="dt-act danger"
              onClick={(e)=>{e.stopPropagation(); setComment(comment); setDeleteOpen(true);}}
              aria-label="Delete comment"
            >
              <TrashIcon/>
            </button>
          </Tooltip>
        ) : null;

      default:
        return null;
    }
  };

  return (
    <div className="dt">
      {toolbar && <div className="dt-toolbar">
        <div className="dt-toolbar-start">{toolbar}</div>
      </div>}

      <div className="dt-scroll">
        {loading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading comments"/></div>
        ) : comments.length > 0 ? (
          <table className="dt-table">
            <thead>
              <tr>
                {headers.map((header)=>(
                  <th
                    key={header.id}
                    scope="col"
                    className={cellClass(header)}
                    style={pinStyle(header.id)}
                    aria-sort={orderBy === header.id ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {header.id !== 'delete' ? (
                      <SortButton
                        label={header.id === 'is_unread' ? <span className="visually-hidden">Status</span> : header.label}
                        active={orderBy === header.id}
                        order={order}
                        onClick={()=>handleRequestSort(header.id)}
                      />
                    ) : header.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {comments.map((comment:any, i:number)=>(
                <tr
                  key={comment?.id ?? i}
                  className={`dt-clickable ${comment?.is_unread ? 'dt-unread' : ''}`}
                  onClick={()=>openComment(comment)}
                >
                  {headers.map((header)=>(
                    <td key={header.id} className={cellClass(header)} style={pinStyle(header.id)}>
                      {renderCell(header.id, comment)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state">
            <div className="dt-empty-title">No Comments Found</div>
          </div>
        )}
      </div>

      <DataTableFoot
        shown={comments?.length ?? 0}
        total={total}
        noun={total === 1 ? 'Comment' : 'Comments'}
        page={page}
        totalPages={totalPages}
        loading={loading}
        onPage={goToPage}
      />
    </div>
  );
};

export default CommentsTable;
